if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const btn = mediaToSwitch.querySelector('button');
          btn.addEventListener('click', this.maybeSwitchVariant.bind(this, mediaToSwitch));
          btn.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        this.enableInfiniteLoop();
      }

      // If the thumbnail is bound to a product variant (data-variant-option-value),
      // switch to that variant by dispatching change on the matching input/select.
      maybeSwitchVariant(thumbnail) {
        const targetValue = thumbnail.dataset.variantOptionValue;
        if (!targetValue) return;

        const productInfo = this.closest('product-info') || document;
        const variantSelects = productInfo.querySelector('variant-selects, variant-radios');
        if (!variantSelects) return;

        // Radio inputs: find matching value, check it, fire change.
        const radio = Array.from(variantSelects.querySelectorAll('input[type="radio"]'))
          .find((r) => r.value === targetValue);
        if (radio) {
          if (radio.checked) return;
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        // Select dropdowns: find matching option, set value, fire change.
        const select = Array.from(variantSelects.querySelectorAll('select'))
          .find((s) => Array.from(s.options).some((o) => o.value === targetValue));
        if (select && select.value !== targetValue) {
          select.value = targetValue;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      // Fade gallery: prev/next cycle the is-active slide instead of scrolling. Wraps at both ends.
      enableInfiniteLoop() {
        const viewer = this.elements.viewer;
        if (!viewer || !viewer.prevButton || !viewer.nextButton) return;

        viewer.enableSliderLooping = true;
        viewer.prevButton.removeAttribute('disabled');
        viewer.nextButton.removeAttribute('disabled');

        const cycle = (dir) => {
          const slides = Array.from(viewer.querySelectorAll('.product__media-item'));
          if (slides.length < 2) return;
          const currentIdx = slides.findIndex((s) => s.classList.contains('is-active'));
          const nextIdx = (currentIdx + dir + slides.length) % slides.length;
          const targetId = slides[nextIdx].dataset.mediaId;
          this.setActiveMedia(targetId, false);
        };

        const handleClick = (event) => {
          event.stopImmediatePropagation();
          event.preventDefault();
          cycle(event.currentTarget.name === 'next' ? 1 : -1);
        };

        viewer.prevButton.addEventListener('click', handleClick, true);
        viewer.nextButton.addEventListener('click', handleClick, true);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        const announce = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        // announce immediately for cached images instead of forcing a reload via src reassignment
        if (image.complete) announce();
        else image.addEventListener('load', announce, { once: true });
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
